import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getDataAPI, patchDataAPI, postDataAPI } from '../../../Helpers/FetchAPI';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkbookV2Item } from '../../../components/WorkbookV2/WorkbookV2Item';
import logo from '../../../images/logo/pure_desire_logo_black.svg';
import { MagnifyingGlassCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { ImageStack } from '../../../components/❉molecules/ImageStack';
import { WorkbookNavV2 } from '../../../components/🦠organisms/WorkbookNavV2';
import Button from '../../../components/Button';
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { ToolDrawer } from '../../../components/ToolDrawer/ToolDrawer';
import useUser from '../../../Hooks/useUser';
import toast from 'react-hot-toast';
import { ONE_TOAST_ID } from '../../../constant';
import { ToolDrawerNote } from '../../../components/ToolDrawer/ToolDrawerNote';
import ToolDrawerVideo from '../../../components/ToolDrawer/ToolDrawerVideo';
import ToolDrawerHighlights from '../../../components/ToolDrawer/ToolDrawerHighlights';
import { useDebounce, useWindowSize } from 'react-use';
import { ToolDrawerTools } from '../../../components/ToolDrawer/ToolDrawerTools';
import roman from '@sguest/roman-js';
import { MobilePageSearch } from './mobilePageSearch';
import { Swipeable } from '../../../components/⚛️atoms/Swipeable';
import { LanguageSelect } from '../../../components/LanguageSelect';
import { useTranslation } from 'react-i18next';
import { formatExpirationDate, isCreditExpired } from '../../../utils/creditExpiration';
import { CreditExpirationBadge } from '../../../components/⚛️atoms/CreditExpirationBadge';
import {
  getLessonByPageApiPath,
  getLessonByTemplateApiPath,
  getTemplateItemId,
  getTemplateLessonId,
  getWorkbookByCreditApiPath
} from '../../../utils/workbookV2';
import { useMutation } from '@tanstack/react-query';
import { handleExpiredCreditError } from '../../../utils/creditErrorHandler';
import Certification from '../../../components/molecules/clientWorbook/certification.js';

const BookV2 = () => {
  const { id: creditId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [workbook, setWorkbook] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [endOfWorkbook, setEndOfWorkbook] = useState(false);
  const [pageQuery, setPageQuery] = useState(null);
  const [lessonPageRange, setLessonPageRange] = useState(null);
  const [showToolDrawer, setShowToolDrawer] = useState(false);
  const [currentToolDrawer, setCurrentToolDrawer] = useState('Notes');
  const [certLoading, setCertLoading] = useState(false);
  const [certification, setCertification] = useState('');
  const [videoUrls, setVideoUrls] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [pendingLessonId, setPendingLessonId] = useState(null);
  const [loaderSweepActive, setLoaderSweepActive] = useState(false);
  const [loaderSweepCycle, setLoaderSweepCycle] = useState(0);
  const { width: pageWidth } = useWindowSize();
  const { t } = useTranslation();

  const lessonContentRef = useRef(null);
  const loadingProgressResetRef = useRef(null);
  const loaderSweepIntervalRef = useRef(null);
  const loaderSweepFrameRef = useRef(null);
  const navigationRequestRef = useRef(0);
  const itemRefs = useMemo(() => {
    const refs = {};
    activeLesson?.items?.forEach((item) => {
      const itemId = getTemplateItemId(item);

      if (itemId) {
        refs[itemId] = React.createRef(null);
      }
    });
    return refs;
  }, [activeLesson]);

  const routeLessonId = new URLSearchParams(location.search).get('lesson');

  const buildLessonRoute = (templateLessonId) => {
    if (!templateLessonId) {
      return `/library/v2/${creditId}`;
    }

    return `/library/v2/${creditId}?lesson=${templateLessonId}`;
  };

  const updatePageNumbers = (items) => {
    const pageNumbers = items
      .flatMap((i) => i.page_number)
      .map((pn) => {
        const num = parseInt(pn);
        if (!isNaN(num)) return { num: num, text: pn };
        const parseRoman = roman.parseRoman(pn);
        return { num: parseRoman, text: pn };
      });
    pageNumbers.sort((a, b) => a.num - b.num);
    if (!pageNumbers.length) return;
    const firstPage = pageNumbers[0].text;
    const lastPage = pageNumbers[pageNumbers.length - 1].text;
    setLessonPageRange(`${firstPage} - ${lastPage}`);
  };

  const clearPendingNavigation = () => {
    if (loaderSweepIntervalRef.current) {
      window.clearInterval(loaderSweepIntervalRef.current);
      loaderSweepIntervalRef.current = null;
    }
    if (loaderSweepFrameRef.current) {
      window.cancelAnimationFrame(loaderSweepFrameRef.current);
      loaderSweepFrameRef.current = null;
    }
    setLoaderSweepActive(false);
    if (loadingProgressResetRef.current) {
      window.clearTimeout(loadingProgressResetRef.current);
      loadingProgressResetRef.current = null;
    }
    loadingProgressResetRef.current = window.setTimeout(() => {
      setPendingLessonId(null);
      loadingProgressResetRef.current = null;
    }, 140);
  };

  useEffect(() => {
    if (!pendingLessonId) {
      if (loaderSweepIntervalRef.current) {
        window.clearInterval(loaderSweepIntervalRef.current);
        loaderSweepIntervalRef.current = null;
      }
      if (loaderSweepFrameRef.current) {
        window.cancelAnimationFrame(loaderSweepFrameRef.current);
        loaderSweepFrameRef.current = null;
      }
      setLoaderSweepActive(false);
      return;
    }

    const startSweep = () => {
      setLoaderSweepCycle((current) => current + 1);
      setLoaderSweepActive(false);
      if (loaderSweepFrameRef.current) {
        window.cancelAnimationFrame(loaderSweepFrameRef.current);
      }
      loaderSweepFrameRef.current = window.requestAnimationFrame(() => {
        loaderSweepFrameRef.current = window.requestAnimationFrame(() => {
          setLoaderSweepActive(true);
          loaderSweepFrameRef.current = null;
        });
      });
    };

    startSweep();
    loaderSweepIntervalRef.current = window.setInterval(startSweep, 1900);

    return () => {
      if (loaderSweepIntervalRef.current) {
        window.clearInterval(loaderSweepIntervalRef.current);
        loaderSweepIntervalRef.current = null;
      }
      if (loaderSweepFrameRef.current) {
        window.cancelAnimationFrame(loaderSweepFrameRef.current);
        loaderSweepFrameRef.current = null;
      }
    };
  }, [pendingLessonId]);

  const handleActiveLessonChange = (lessonData, options = {}) => {
    const { replaceRoute = true } = options;

    if (lessonData?.end_of_workbook) {
      clearPendingNavigation();
      clearToolDrawerContextItems();
      setLessonPageRange(null);
      setActiveLesson(null);
      setEndOfWorkbook(true);

      if (routeLessonId) {
        navigate(buildLessonRoute(null), { replace: true });
      }

      return;
    }

    const nextTemplateLessonId = getTemplateLessonId(lessonData);
    const redirectedFromTemplateLessonId = lessonData?.redirected_from_template_lesson_id;

    updatePageNumbers(lessonData.items ?? []);
    clearToolDrawerContextItems();
    setEndOfWorkbook(false);
    setActiveLesson(lessonData);
    if (lessonContentRef.current) {
      lessonContentRef.current.scrollTo(0, 0);
    }
    clearPendingNavigation();

    if (
      nextTemplateLessonId &&
      (routeLessonId !== nextTemplateLessonId || redirectedFromTemplateLessonId)
    ) {
      navigate(buildLessonRoute(nextTemplateLessonId), { replace: replaceRoute });
    }
  };

  const updateWorkbook = () => {
    return getDataAPI(getWorkbookByCreditApiPath(creditId)).then((res) => {
      setWorkbook(res.data);
      return res.data;
    });
  };

  const clearToolDrawerContextItems = () => {
    setVideoUrls([]);
    setHighlights([]);
  };

  useEffect(() => {
    if (endOfWorkbook) return;

    const activeTemplateLessonId = getTemplateLessonId(activeLesson);

    if (workbook && routeLessonId && routeLessonId === activeTemplateLessonId && !pendingLessonId) {
      return () => {
        clearPendingNavigation();
      };
    }

    updateWorkbook().then((nextWorkbook) => {
      const requestedLessonId = routeLessonId;
      const nextLessonId = requestedLessonId
        ? requestedLessonId
        : getTemplateLessonId(
            nextWorkbook.chapters
              .flatMap((chapter) => chapter.lessons)
              .find((lesson) => lesson.completed === false)
          );

      if (!nextLessonId) {
        handleActiveLessonChange({ end_of_workbook: true });
        return;
      }

      const requestId = ++navigationRequestRef.current;
      getDataAPI(getLessonByTemplateApiPath(creditId, nextLessonId)).then((response) => {
        if (requestId !== navigationRequestRef.current) {
          return;
        }
        handleActiveLessonChange(response.data, { replaceRoute: !requestedLessonId });
      })
        .catch((e) => {
          if (e.response?.status === 404) {
            toast.error('The requested lesson was not found. Redirecting to the first lesson.');
            navigate(buildLessonRoute(null), { replace: true });
          }
        });
    });
    return () => {
      clearPendingNavigation();
    };
  }, [creditId, routeLessonId]);

  const changeActiveLesson = (templateLessonId, options = {}) => {
    if (!templateLessonId || templateLessonId === getTemplateLessonId(activeLesson)) {
      return;
    }

    if (loadingProgressResetRef.current) {
      window.clearTimeout(loadingProgressResetRef.current);
      loadingProgressResetRef.current = null;
    }
    setPendingLessonId(templateLessonId);

    const requestId = ++navigationRequestRef.current;
    getDataAPI(getLessonByTemplateApiPath(creditId, templateLessonId)).then((res) => {
      if (requestId !== navigationRequestRef.current) {
        return;
      }
      handleActiveLessonChange(res.data, options);
    });
  };

  const getLessonPosition = (lesson = activeLesson) => {
    if (!workbook || !lesson) {
      return { chapterIndex: -1, lessonIndex: -1 };
    }

    const lessonTemplateLessonId = getTemplateLessonId(lesson);
    const chapterIndex = workbook.chapters.findIndex((chapter) => {
      return chapter.lessons.findIndex((chapterLesson) => getTemplateLessonId(chapterLesson) === lessonTemplateLessonId) >= 0;
    });

    if (chapterIndex < 0) {
      return { chapterIndex: -1, lessonIndex: -1 };
    }

    return {
      chapterIndex,
      lessonIndex: workbook.chapters[chapterIndex].lessons.findIndex(
        (chapterLesson) => getTemplateLessonId(chapterLesson) === lessonTemplateLessonId
      )
    };
  };

  const handlePrev = () => {
    const { chapterIndex, lessonIndex } = getLessonPosition();

    if (chapterIndex < 0 || lessonIndex < 0) {
      return;
    }

    if (lessonIndex > 0) {
      changeActiveLesson(getTemplateLessonId(workbook.chapters[chapterIndex].lessons[lessonIndex - 1]));
    } else if (chapterIndex > 0) {
      const prevChapter = workbook.chapters[chapterIndex - 1];
      changeActiveLesson(getTemplateLessonId(prevChapter.lessons[prevChapter.lessons.length - 1]));
    }
  };

  const handleNext = () => {
    const checks = [];
    for (const ref in itemRefs) {
      if (itemRefs[ref].current) {
        checks.push(itemRefs[ref].current.checkAnswers());
      }
    }
    if (checks.some((c) => !c)) {
      toast.error('Please correct all the flagged items before proceeding');
      return;
    }
    if (!activeLesson.completed) {
      toast
        .promise(
          patchDataAPI(getLessonByTemplateApiPath(creditId, getTemplateLessonId(activeLesson)), { completed: true }),
          {
            loading: t('Saving Progress'),
            success: t('Progress Saved'),
            error: t('Error Saving Progress')
          },
          { id: ONE_TOAST_ID }
        )
        .then(() => {
          updateWorkbook();
        });
    }
    const { chapterIndex, lessonIndex } = getLessonPosition();

    if (chapterIndex < 0 || lessonIndex < 0) {
      return;
    }

    if (lessonIndex < workbook.chapters[chapterIndex].lessons.length - 1) {
      changeActiveLesson(getTemplateLessonId(workbook.chapters[chapterIndex].lessons[lessonIndex + 1]));
    } else if (chapterIndex < workbook.chapters.length - 1) {
      const nextChapter = workbook.chapters[chapterIndex + 1];
      changeActiveLesson(getTemplateLessonId(nextChapter.lessons[0]));
    } else {
      handleActiveLessonChange({ end_of_workbook: true });
    }
  };

  useDebounce(
    () => {
      if (!pageQuery || !workbook?.credit_id) return;
      const requestId = ++navigationRequestRef.current;
      getDataAPI(getLessonByPageApiPath(workbook.credit_id, pageQuery)).then((res) => {
        if (requestId !== navigationRequestRef.current) {
          return;
        }
        if (!res.data) {
          setPageQuery('');
          return;
        }
        handleActiveLessonChange(res.data);
        setPageQuery('');
      });
    },
    250,
    [pageQuery, workbook?.credit_id]
  );

  const generateCertificateMutation = useMutation({
    mutationFn: async (params) => {
      return await postDataAPI('/userWorkbookItems/generateCert', params);
    },
    onSuccess: () => {
      setCertLoading(false);
      setCertification(true);
    },
    onError: (err) => {
      setCertLoading(false);
      if (!handleExpiredCreditError(err)) {
        alert(err.message);
      }
    }
  });

  useEffect(() => {
    if (!endOfWorkbook || !workbook || !currentUser || certification) return;

    setCertLoading(true);
    generateCertificateMutation.mutate({
      user: {
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        _id: currentUser._id
      },
      workbook: {
        title: workbook.title,
        _id: workbook._id,
        type: workbook.type
      },
      credit_id: creditId
    });
  }, [endOfWorkbook]);

  useEffect(() => {
    return () => {
      clearPendingNavigation();
    };
  }, []);

  if (!workbook && !activeLesson && !endOfWorkbook) {
    return <span>{t("LOADING...")}</span>;
  }

  // Check if credit has expired - block access if expired
  if (workbook && isCreditExpired(workbook)) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-6 rounded-xl bg-white p-12 shadow-lg">
          <h1 className="text-4xl font-bold text-gray-900">Credit Expired</h1>
          <p className="text-center text-lg text-gray-600">
            This credit expired on {formatExpirationDate(workbook)}.<br />
          </p>
          <a
            href="/user/library"
            className="rounded-lg bg-pddarkblue px-6 py-3 text-lg font-semibold text-white hover:bg-pddarkblue/90"
          >
            Go Back to Library
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-pdteal/5 h-screen">
      <div className="sticky top-0 flex max-w-screen justify-between p-3">
        <a href="/" className="row-span-all hidden lg:block">
          <img src={logo} alt="Logo" className="w-70" />
        </a>
        <div className="flex items-center gap-5 lg:-ml-[6dvw]">
          <ImageStack
            percentage={workbook.completed_lessons ? (workbook.completed_lessons / workbook.total_lessons) * 100 : 0}
            file={workbook?.cover_image}
            alt="Cover"
            badge={false}
            className="h-[6dvh] w-[6dvh]"
          />
          <div className="flex flex-col gap-2">
            <span className="font-title max-w-[60dvw] truncate text-4xl font-normal whitespace-pre text-gray-900 lg:text-5xl">
              {workbook?.title
                .split(':')
                .map((s) => s.trim())
                .join(':\n')}
            </span>
            {workbook && (
              <div className="flex items-center">
                <CreditExpirationBadge credit={workbook} showDate={true} compact={true} />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-5">
          <LanguageSelect />
          <a
            href="/"
            className="text-pddarkblue joyride-exit col-span-2 col-start-3 flex items-center justify-center gap-2 justify-self-end rounded-lg p-3 lg:col-start-4 lg:row-start-1 lg:justify-self-end"
          >
            {pageWidth > 900 && <span className="text-xl whitespace-nowrap lg:text-2xl">{t('Save & Exit')}</span>}
            <XCircleIcon className="size-10" />
          </a>
        </div>
      </div>
      <div
        className="fixed top-[9.1dvh] left-[2dvw] flex h-[90dvh] w-[96dvw] items-center justify-between transition-all duration-300 lg:w-[68dvw] data-[full=true]:lg:left-[16dvw]"
        data-full={!showToolDrawer}
      >
        <Button
          variant="primary"
          onClick={handlePrev}
          className="group absolute bottom-0 flex items-center lg:static lg:-ml-15 lg:flex-col lg:items-end"
        >
          <ChevronDoubleLeftIcon className="size-10" />
          <span className="text-pddarkblue/80 text-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 data-[show=true]:opacity-100">
            {t('Previous')}
          </span>
        </Button>
        <div
          className="no-scrollbar -mt-16 h-92/100 w-full overflow-x-hidden overflow-y-scroll rounded-xl bg-white px-10 pb-10 shadow-md lg:mt-0 lg:h-full"
          id="workbookContent"
          ref={lessonContentRef}
          data-full={!showToolDrawer}
        >
          <Swipeable onSwipeLeft={handleNext} onSwipeRight={handlePrev} propigate={true}>
            <div className="joyride-nav sticky top-0 z-50 flex w-full flex-col justify-between bg-white">
              <span className="absolute top-10 right-0 text-lg whitespace-nowrap text-gray-400">
                p. {lessonPageRange}
              </span>
              <label className="absolute top-1/2 right-30 flex w-80 -translate-y-1/2 items-center gap-2 whitespace-nowrap">
                <MagnifyingGlassCircleIcon className="text-pddarkblue/70 absolute top-1/2 left-1 size-8 -translate-y-1/2" />
                <input
                  className="focus:outline-pdteal block w-full rounded-md bg-white px-6 py-3 pl-10 text-2xl text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-2xl/6"
                  placeholder={t('Page Search...')}
                  value={pageQuery}
                  onChange={(e) => setPageQuery(e.target.value)}
                />
              </label>
              <div className="-ml-10 min-h-max w-[96dvw] bg-white lg:w-[60%]">
                <WorkbookNavV2
                  workbook={workbook}
                  activeLesson={activeLesson}
                  pendingLessonId={pendingLessonId}
                  isNavigating={Boolean(pendingLessonId)}
                  onClick={(data) => {
                    changeActiveLesson(data);
                  }}
                  pageRange={lessonPageRange}
                ></WorkbookNavV2>
              </div>
              <div className="-ml-10 h-px w-screen bg-gray-200"></div>
              <div
                className="-ml-10 h-[2px] w-screen overflow-hidden bg-transparent opacity-0 transition-opacity duration-200 data-[pending=true]:opacity-100"
                data-pending={Boolean(pendingLessonId)}
              >
                <div
                  key={loaderSweepCycle}
                  className={`h-full w-80 rounded-full bg-pdteal transition-transform duration-[2s] ease-in-out will-change-transform ${
                    loaderSweepActive ? 'translate-x-[calc(100vw+20rem)]' : '-translate-x-full'
                  }`}
                />
              </div>
            </div>
            <div className="relative pt-5">
              {endOfWorkbook ? (
                <div className="flex min-h-[50dvh] flex-col items-center justify-center gap-6 xl:gap-3 pt-4 md:pt-8 text-center">
                  <h1 className="text-5xl font-bold text-gray-900">{t('Workbook Complete')}</h1>
                  <p className="max-w-2xl text-xl text-gray-600">
                    {t('You have reached the end of this workbook. Save your progress and continue with your next step from the library.')}
                  </p>
                  {certLoading && <p className="text-2xl md:text-3xl py-48 md:py-96 text-pdorange font-bold">{t('Generating your certificate...')}</p>}
                  {!certLoading && certification && (
                    <Certification user={currentUser} workbook={workbook} />
                  )}
                  <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
                    <a href="/library" className="rounded-lg bg-pddarkblue px-6 py-3 text-xl font-semibold text-white">
                      {t('Back to Library')}
                    </a>
                    <a href="/" className="rounded-lg border border-gray-300 px-6 py-3 text-xl font-semibold text-gray-700">
                      {t('Save & Exit')}
                    </a>
                  </div>
                </div>
              ) : (
                activeLesson?.items?.map((item) => {
                  const templateItemId = getTemplateItemId(item);

                  return (
                    <WorkbookV2Item
                      ref={itemRefs[templateItemId]}
                      items={activeLesson.items}
                      key={templateItemId}
                      item={item}
                      userId={activeLesson.user_id}
                      creditId={creditId}
                      lessonId={activeLesson?._id}
                      setVideoUrls={setVideoUrls}
                      setLessonHighlights={setHighlights}
                      openVideoDrawer={() => {
                        setCurrentToolDrawer('Videos');
                        setShowToolDrawer(true);
                      }}
                      workbook={workbook}
                    />
                  );
                })
              )}
            </div>
          </Swipeable>
        </div>
        <Button
          variant="primary"
          onClick={handleNext}
          className="group absolute right-0 bottom-0 flex flex-row-reverse items-center lg:static lg:-mr-3 lg:flex-col lg:items-start"
        >
          <ChevronDoubleRightIcon className="size-10" />
          <span className="text-pddarkblue/80 text-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 data-[show=true]:opacity-100">
            {t("Next")}
          </span>
        </Button>
      </div>
      {pageWidth < 900 && <MobilePageSearch setPageQuery={setPageQuery} />}
      <ToolDrawer
        title={currentToolDrawer}
        open={showToolDrawer}
        setOpen={setShowToolDrawer}
        setCurrentToolDrawer={setCurrentToolDrawer}
      >
        {currentToolDrawer === 'Notes' && <ToolDrawerNote user={currentUser} creditId={creditId} workbookId={workbook._id} />}
        {currentToolDrawer === 'Videos' && (
          <div className="no-scrollbar flex h-full w-full flex-col items-center gap-5 overflow-y-scroll pt-5">
            {videoUrls.map((videoUrl, index) => (
              <ToolDrawerVideo videoUrl={videoUrl} key={videoUrl + index} />
            ))}
            {videoUrls.length === 0 && (
              <div className="flex h-1/4 items-center justify-center gap-5">
                <span className="text-xl font-bold">{t("No Videos For this Lesson")}</span>
              </div>
            )}
          </div>
        )}
        {currentToolDrawer === 'Highlights' && <ToolDrawerHighlights highlights={highlights} />}
        {currentToolDrawer === 'Tools' && <ToolDrawerTools />}
      </ToolDrawer>
    </div>
  );
};

export default BookV2;
